import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { findArticleById, articles } from '@/mocks/articles';
import { findWorkById } from '@/mocks/works';
import { useEffect } from 'react';

export default function ArticlePage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const article = findArticleById(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  if (!article) {
    return (
      <Layout>
        <div className="mx-auto max-w-7xl px-4 py-24 text-center md:px-6">
          <i className="ri-file-search-line text-5xl text-foreground-400" aria-hidden="true"></i>
          <p className="mt-4 text-lg text-foreground-600">文章不存在或已下线</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-home-4-line" aria-hidden="true"></i>
            {t('btn_back_home')}
          </Link>
        </div>
      </Layout>
    );
  }

  const relatedWorks = article.relatedWorks.map(findWorkById).filter(Boolean) as NonNullable<ReturnType<typeof findWorkById>>[];
  const otherArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);
  const relatedArticles = article.relatedWorks.length
    ? otherArticles
    : articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <Layout>
      {/* Article JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            datePublished: article.date,
            author: { '@type': 'Organization', name: article.author },
            publisher: { '@type': 'Organization', name: '动漫网' },
          }),
        }}
      />

      <article className="mx-auto max-w-4xl px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: '文章资讯', path: `/article/${article.id}` }, { label: article.title }]} />

        <header className="mt-6 text-center">
          <span className="rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-700">
            {article.category}
          </span>
          <h1 className="mx-auto mt-4 max-w-2xl font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" aria-hidden="true"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {t('art_time')} {article.date}
            </span>
          </div>
        </header>

        <div className="mt-8 overflow-hidden rounded-lg border border-background-200/70">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 动漫网`}
            className="aspect-[16/9] w-full object-cover object-top"
          />
        </div>

        {/* 正文 */}
        <div className="mt-8 space-y-5 text-[15px] leading-7 text-foreground-800">
          {article.content.map((para, idx) => (
            <p key={idx}>{para}</p>
          ))}
        </div>

        {/* 标签 */}
        <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900"
            >
              #{tag}
            </span>
          ))}
        </div>

        {/* 相关作品 */}
        {relatedWorks.length > 0 && (
          <section className="mt-10" aria-label={t('art_related')}>
            <h2 className="font-heading text-xl text-foreground-950">{t('art_related')}</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {relatedWorks.map((work) => (
                <Link
                  key={work.id}
                  to={`/category/${work.categories[0]}`}
                  className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition hover:border-primary-300"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-background-100">
                    <img
                      src={work.cover}
                      alt={work.title}
                      title={`${work.title} 动漫网`}
                      className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="truncate p-2 text-sm font-medium text-foreground-950 group-hover:text-primary-600">
                    {work.title}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* 相关文章 */}
        <section className="mt-10" aria-label={t('art_latest')}>
          <h2 className="font-heading text-xl text-foreground-950">{t('art_latest')}</h2>
          <div className="mt-4 space-y-3">
            {relatedArticles.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.id}`}
                className="flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3 transition hover:border-primary-300"
              >
                <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-100">
                  <img
                    src={a.cover}
                    alt={a.title}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-600">
                    {a.title}
                  </h3>
                  <p className="mt-1 flex items-center gap-3 text-xs text-foreground-600">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line" aria-hidden="true"></i>
                      {a.date}
                    </span>
                    <span>{a.category}</span>
                  </p>
                </div>
                <i className="ri-arrow-right-s-line shrink-0 text-foreground-400" aria-hidden="true"></i>
              </Link>
            ))}
          </div>
        </section>
      </article>
    </Layout>
  );
}